import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bell, BellOff, X } from 'lucide-react';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { useUser } from '@core/contexts/ProfileContext';
import { useWebPush } from '@core/hooks/useWebPush';
import { PUSH_PROMPT_DISMISS_KEY, browserPushDeps, resyncWebPush, shouldShowPushPrompt, type EnableResult } from '@core/utils/webPush';

// Тексты — общие для карточки на главной и раздела в «Моих данных».
export const IOS_INSTALL_HINT =
  'На iPhone и iPad уведомления приходят только в приложении на экране «Домой»: в Safari нажмите «Поделиться» → «На экран «Домой»», откройте приложение с иконки и включите уведомления.';
const DENIED_HINT =
  'Уведомления запрещены в настройках браузера. Разрешите их для этого сайта и вернитесь сюда.';

function enableToast(r: EnableResult | 'error') {
  switch (r) {
    case 'subscribed':
      toast.success('Уведомления включены');
      break;
    case 'denied':
      toast.error('Браузер не разрешил уведомления');
      break;
    case 'disabled':
      toast.error('Уведомления в браузере пока недоступны');
      break;
    case 'unsupported':
      toast.error('Этот браузер не поддерживает уведомления');
      break;
    default:
      toast.error('Не удалось включить уведомления. Попробуйте ещё раз');
  }
}

function readDismissedAt(): number | null {
  try {
    const at = Number(localStorage.getItem(PUSH_PROMPT_DISMISS_KEY));
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}

/**
 * Карточка на главной PWA после входа: предложить уведомления о баллах, наградах и подарках. Системное окно
 * разрешения — только по нажатию «Включить». На iPhone в Safari — подсказка про экран «Домой». В мини-аппе
 * Telegram не показывается (capabilities.webPush нет). Включённую подписку при запуске досылает на сервер.
 */
export function WebPushPrompt() {
  const { user } = useUser();
  const push = useWebPush(user?.id);
  const [dismissedAt, setDismissedAt] = useState(readDismissedAt);

  useEffect(() => {
    if (user?.id && push.availability === 'available') void resyncWebPush(user.id, browserPushDeps());
  }, [user?.id, push.availability]);

  const mode = shouldShowPushPrompt({
    availability: push.availability,
    serverEnabled: push.serverEnabled,
    on: push.on,
    permission: typeof Notification !== 'undefined' ? Notification.permission : null,
    dismissedAt,
    now: Date.now(),
  });
  if (!user || !mode) return null;

  const dismiss = () => {
    const now = Date.now();
    setDismissedAt(now);
    try {
      localStorage.setItem(PUSH_PROMPT_DISMISS_KEY, String(now));
    } catch {
      // без localStorage — скроем до перезапуска
    }
  };

  return (
    <Card className="!p-3 bg-primary/5 border-primary/20">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Bell className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 text-sm text-text-main">
          <div className="font-semibold mb-1">Уведомления о баллах и подарках</div>
          {mode === 'ios-install' ? (
            <div className="text-text-muted">{IOS_INSTALL_HINT}</div>
          ) : (
            <>
              <div className="text-text-muted mb-2">
                Сообщим о начислениях, наградах и подарках, даже когда приложение закрыто.
              </div>
              <Button size="sm" disabled={push.busy} onClick={() => void push.enable().then(enableToast)}>
                Включить
              </Button>
            </>
          )}
        </div>
        <button
          type="button"
          aria-label="Скрыть"
          onClick={dismiss}
          className="p-1 -mr-1 text-text-muted rounded-full hover:bg-secondary/50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </Card>
  );
}

/** «Уведомления в браузере» в профиле («Мои данные»): включить/выключить, подсказки для iPhone и запрета. */
export function WebPushSettings() {
  const { user } = useUser();
  const push = useWebPush(user?.id);
  if (!user || push.availability === 'off' || push.serverEnabled !== true) return null;

  let body;
  switch (push.availability) {
    case 'ios-install':
      body = <p className="text-sm text-text-muted">{IOS_INSTALL_HINT}</p>;
      break;
    case 'unsupported':
      body = <p className="text-sm text-text-muted">Этот браузер не поддерживает уведомления.</p>;
      break;
    case 'denied':
      body = <p className="text-sm text-text-muted">{DENIED_HINT}</p>;
      break;
    default:
      body = (
        <>
          <p className="text-sm text-text-muted mb-3">
            {push.on
              ? 'Включены: сообщим о начислениях, наградах и подарках, даже когда приложение закрыто.'
              : 'Сообщим о начислениях, наградах и подарках, даже когда приложение закрыто.'}
          </p>
          {push.on ? (
            <Button
              variant="outline"
              size="sm"
              disabled={push.busy}
              onClick={() =>
                void push.disable().then((ok) =>
                  ok ? toast.success('Уведомления выключены') : toast.error('Не удалось выключить. Попробуйте ещё раз'),
                )
              }
            >
              Выключить
            </Button>
          ) : (
            <Button size="sm" disabled={push.busy} onClick={() => void push.enable().then(enableToast)}>
              Включить
            </Button>
          )}
        </>
      );
  }

  return (
    <Card>
      <h2 className="font-bold mb-2 flex items-center gap-2">
        {push.on ? <Bell className="w-4 h-4 text-primary" /> : <BellOff className="w-4 h-4 text-text-muted" />}
        Уведомления в браузере
      </h2>
      {body}
    </Card>
  );
}
